import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/apiClient";
import AddressFields, { isAddressComplete } from "@/components/app/AddressFields";
import { toast } from "sonner";
import { UserCog, Save } from "lucide-react";

const EMPTY_ADDR = { cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" };

export default function CustomerEditDialog({ customer, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", document: "" });
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setForm({ name: customer.name || "", email: customer.email || "", phone: customer.phone || "", document: customer.document || "" });
      setAddr({ ...EMPTY_ADDR, ...(customer.address || {}) });
    }
  }, [open, customer]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Preencha nome, e-mail e telefone."); return;
    }
    if (!isAddressComplete(addr)) {
      toast.error("Endereço é obrigatório (CEP, rua, cidade e UF)."); return;
    }
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        document: form.document.trim() || "",
        address: {
          cep: addr.cep, street: addr.street, number: addr.number || null,
          complement: addr.complement || null, neighborhood: addr.neighborhood || null,
          city: addr.city, state: addr.state,
        },
      });
      toast.success("Cliente atualizado!");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="customer-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-[#1e7be0]" /> Editar cliente</DialogTitle>
          <DialogDescription>Atualize os dados e o endereço do cliente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Nome completo *</Label>
            <Input value={form.name} onChange={upd("name")} data-testid="customer-edit-name-input" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>E-mail *</Label>
              <Input type="email" value={form.email} onChange={upd("email")} data-testid="customer-edit-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefone *</Label>
              <Input value={form.phone} onChange={upd("phone")} data-testid="customer-edit-phone-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>CPF / CNPJ (opcional)</Label>
            <Input value={form.document} onChange={upd("document")} data-testid="customer-edit-document-input" /></div>
          <div className="pt-1">
            <Label className="text-sm font-semibold text-slate-700">Endereço *</Label>
            <div className="mt-2"><AddressFields value={addr} onChange={setAddr} idPrefix="customer-edit-addr" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy} data-testid="customer-edit-save-button">
              <Save className="h-4 w-4" /> {busy ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
